import template from "./template.html";
import "./styles.scss";
import closeIconUrl from "./icons/close.svg";
import lineIconUrl from "./icons/line.svg";
import telegramIconUrl from "./icons/telegram.svg";
import xIconUrl from "./icons/x.svg";
import whatsappIconUrl from "./icons/whatsapp.svg";
import chatIconUrl from "./icons/chat.svg";
import chatBubbleIconUrl from "./icons/chatBubble.svg";
const SCRIPT_ELEMENT = document.currentScript;
const SCRIPT_SOURCE = SCRIPT_ELEMENT?.src ?? "";
const SCRIPT_VERSION = getVersionFromUrl(SCRIPT_SOURCE);
const DEFAULT_LINKS = {
    line: "https://page.line.me/?accountId=786azhee",
    telegram: "http://t.me/ThinkMarkets_Global_bot",
    x: "https://twitter.com/messages/compose?recipient_id=136399657",
    whatsapp: "https://wa.me/971527961677",
};
const DEFAULT_CONFIG = {
    lineUrl: DEFAULT_LINKS.line,
    locale: "en",
    zendeskKey: "f0585f41-3237-4e8e-aaed-96c64eee884c",
};
const mergedConfig = {
    ...DEFAULT_CONFIG,
    ...(window.tmSocialSupportWidgetConfig ?? {}),
};
window.tmSocialSupportWidgetConfig = undefined;
const socialLinks = {
    line: mergedConfig.lineUrl ?? DEFAULT_LINKS.line,
    telegram: mergedConfig.telegramUrl ?? DEFAULT_LINKS.telegram,
    x: mergedConfig.xUrl ?? DEFAULT_LINKS.x,
    whatsapp: mergedConfig.whatsappUrl ?? DEFAULT_LINKS.whatsapp,
};
const iconUrls = {
    close: closeIconUrl,
    line: lineIconUrl,
    telegram: telegramIconUrl,
    x: xIconUrl,
    whatsapp: whatsappIconUrl,
    chat: chatIconUrl,
    chatBubble: chatBubbleIconUrl,
};
const ABSOLUTE_URL_PATTERN = /^(?:https?:)?\/\//;
const ROOT_ID = "social-channels";
let setPanelVisibility;
let globalClickHandlerAttached = false;
onDocumentReady(() => {
    ensureStylesheet();
    const root = mountWidget();
    if (!root) {
        return;
    }
    applyIcons(root);
    configureLinks(root);
    wireInteractions(root);
    attachGlobalLiveChatHandler();
    const ensureZendesk = createZendeskLoader(mergedConfig.zendeskKey ?? DEFAULT_CONFIG.zendeskKey, mergedConfig.locale ?? DEFAULT_CONFIG.locale, Boolean(mergedConfig.debug));
    ensureZendesk().catch((error) => {
        if (mergedConfig.debug) {
            console.warn("Zendesk messenger failed to initialise", error);
        }
    });
    window.OpenWidget = () => {
        ensureZendesk()
            .then((api) => {
            setPanelVisibility?.(true);
            api("messenger", "open");
        })
            .catch((error) => {
            if (mergedConfig.debug) {
                console.warn("Unable to open Zendesk messenger", error);
            }
        });
    };
    window.CloseWidget = () => {
        ensureZendesk()
            .then((api) => {
            api("messenger", "close");
        })
            .catch((error) => {
            if (mergedConfig.debug) {
                console.warn("Unable to close Zendesk messenger", error);
            }
        });
    };
});
function onDocumentReady(callback) {
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", callback, { once: true });
    }
    else {
        callback();
    }
}
function mountWidget() {
    const existing = document.getElementById(ROOT_ID);
    if (existing instanceof HTMLElement) {
        return existing;
    }
    const templateElement = document.createElement("template");
    templateElement.innerHTML = template.trim();
    const fragment = templateElement.content.firstElementChild;
    if (!(fragment instanceof HTMLElement)) {
        return undefined;
    }
    document.body.appendChild(fragment);
    return fragment;
}
function ensureStylesheet() {
    const existingLink = document.querySelector("link[data-social-support-widget=true]");
    if (existingLink) {
        return existingLink;
    }
    const linkElement = document.createElement("link");
    linkElement.rel = "stylesheet";
    linkElement.href = buildAssetUrl("social-and-support-widget.css", mergedConfig.cssVersion);
    linkElement.setAttribute("data-social-support-widget", "true");
    document.head.appendChild(linkElement);
    return linkElement;
}
function buildAssetUrl(fileName, version) {
    const fromScript = resolveAssetFromScript(fileName, version);
    if (fromScript) {
        return fromScript;
    }
    return appendVersion(fileName, version ?? SCRIPT_VERSION);
}
function resolveAssetFromScript(fileName, version) {
    if (!SCRIPT_SOURCE) {
        return undefined;
    }
    try {
        const url = new URL(SCRIPT_SOURCE, window.location.href);
        url.pathname = url.pathname.replace(/[^/]*$/, fileName);
        const versionParam = version ?? SCRIPT_VERSION;
        url.search = versionParam
            ? `?version=${encodeURIComponent(versionParam)}`
            : "";
        return url.pathname + url.search;
    }
    catch (error) {
        if (mergedConfig.debug) {
            console.warn("Failed to resolve asset URL", error);
        }
        return undefined;
    }
}
function appendVersion(fileName, version) {
    if (!version) {
        return fileName;
    }
    const separator = fileName.includes("?") ? "&" : "?";
    return `${fileName}${separator}version=${encodeURIComponent(version)}`;
}
function getVersionFromUrl(url) {
    if (!url) {
        return undefined;
    }
    try {
        return new URL(url).searchParams.get("version") ?? undefined;
    }
    catch (error) {
        return undefined;
    }
}
function applyIcons(root) {
    const iconNodes = root.querySelectorAll("[data-icon]");
    iconNodes.forEach((node) => {
        const key = node.dataset.icon;
        if (!key) {
            return;
        }
        const iconSrc = iconUrls[key];
        if (!iconSrc) {
            return;
        }
        node.innerHTML = "";
        const img = document.createElement("img");
        img.src = resolveAssetUrl(iconSrc);
        img.alt = "";
        img.setAttribute("aria-hidden", "true");
        img.loading = "lazy";
        img.decoding = "async";
        node.appendChild(img);
    });
}
function resolveAssetUrl(assetPath) {
    if (!assetPath || isImmediateAsset(assetPath)) {
        return assetPath;
    }
    if (assetPath.startsWith("widgets/")) {
        return `/dist/${assetPath}`;
    }
    if (!SCRIPT_SOURCE) {
        return assetPath;
    }
    return resolveRelativeAsset(assetPath);
}
function isImmediateAsset(path) {
    return (path.startsWith("data:") ||
        ABSOLUTE_URL_PATTERN.test(path) ||
        path.startsWith("/"));
}
function resolveRelativeAsset(assetPath) {
    try {
        const scriptUrl = new URL(SCRIPT_SOURCE, window.location.href);
        const basePath = scriptUrl.pathname.replace(/[^/]*$/, "");
        return `${basePath}${assetPath}`;
    }
    catch (error) {
        if (mergedConfig.debug) {
            console.warn("Failed to resolve asset path", assetPath, error);
        }
        return assetPath;
    }
}
function configureLinks(root) {
    Object.keys(socialLinks).forEach((key) => {
        const anchor = root.querySelector(`[data-widget-link="${key}"]`);
        if (anchor) {
            if (socialLinks[key] === "none") {
                anchor.remove();
            }
            else {
                anchor.href = socialLinks[key];
            }
        }
    });
}
function wireInteractions(root) {
    const toggleButton = root.querySelector("#live-chat-button");
    const socialLinksContainer = root.querySelector("#social-links");
    const closeButton = root.querySelector("#close-icon");
    const chatToggle = root.querySelector("[data-widget-action='chat']");
    if (!toggleButton || !socialLinksContainer || !closeButton || !chatToggle) {
        return;
    }
    const setOpen = (open) => {
        toggleButton.classList.toggle("live-chat-button-open", open);
        socialLinksContainer.classList.toggle("social-links-open", open);
        toggleButton.setAttribute("aria-expanded", String(open));
    };
    toggleButton.addEventListener("click", () => {
        const isOpen = !socialLinksContainer.classList.contains("social-links-open");
        setOpen(isOpen);
    });
    closeButton.addEventListener("click", () => {
        setOpen(false);
    });
    chatToggle.addEventListener("click", (event) => {
        event.preventDefault();
        window.OpenWidget?.();
    });
    root.addEventListener("click", () => {
        toggleButton.classList.toggle("show");
    });
    setPanelVisibility = setOpen;
}
function attachGlobalLiveChatHandler() {
    if (globalClickHandlerAttached) {
        return;
    }
    const handler = (event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement)) {
            return;
        }
        const trigger = target.closest(".social-contacts__live-chat__text__link");
        if (trigger) {
            event.preventDefault();
            window.OpenWidget?.();
        }
    };
    document.addEventListener("click", handler);
    globalClickHandlerAttached = true;
}
function createZendeskLoader(zendeskKey, locale, debug) {
    let loaderPromise;
    let configured = false;
    const configureMessenger = (api) => {
        if (configured) {
            return;
        }
        try {
            api("messenger:on", "unreadMessages", (count) => {
                if (debug) {
                    console.warn(`Zendesk unread messages: ${count}`);
                }
            });
            api("messenger", "close");
            if (locale) {
                api("messenger:set", "locale", locale);
            }
            configured = true;
        }
        catch (error) {
            if (debug) {
                console.warn("Failed to configure Zendesk messenger", error);
            }
        }
    };
    const waitForZendesk = (resolve, reject) => {
        const startTime = Date.now();
        const timeoutMs = 10000;
        const poll = () => {
            if (window.zE) {
                configureMessenger(window.zE);
                resolve(window.zE);
                return;
            }
            if (Date.now() - startTime > timeoutMs) {
                reject(new Error("Zendesk messenger timed out"));
                return;
            }
            window.setTimeout(poll, 50);
        };
        poll();
    };
    return () => {
        if (window.zE) {
            configureMessenger(window.zE);
            return Promise.resolve(window.zE);
        }
        if (loaderPromise) {
            return loaderPromise;
        }
        loaderPromise = new Promise((resolve, reject) => {
            const existingSnippet = document.getElementById("ze-snippet");
            const beginPolling = () => {
                waitForZendesk(resolve, reject);
            };
            if (!(existingSnippet instanceof HTMLScriptElement)) {
                const script = document.createElement("script");
                script.id = "ze-snippet";
                script.src = `https://static.zdassets.com/ekr/snippet.js?key=${encodeURIComponent(zendeskKey)}`;
                script.async = true;
                script.defer = true;
                script.addEventListener("load", beginPolling, { once: true });
                script.addEventListener("error", () => {
                    reject(new Error("Failed to load Zendesk snippet"));
                });
                document.head.appendChild(script);
            }
            else {
                existingSnippet.addEventListener("load", beginPolling, {
                    once: true,
                });
            }
            beginPolling();
        });
        loaderPromise.catch(() => {
            loaderPromise = undefined;
        });
        return loaderPromise;
    };
}
